import type { BridgeResource, ConnectedDevice } from '../resources/bridge.js';

export type DesktopConnectionState = 'connected' | 'connecting' | 'offline';

export interface DesktopConnection {
  state: DesktopConnectionState;
  desktopDevices: ConnectedDevice[];
}

export const DESKTOP_OFFLINE: DesktopConnection = { state: 'offline', desktopDevices: [] };

export function deriveDesktopConnection(
  devices: readonly ConnectedDevice[],
  reachableDeviceIds: ReadonlySet<string>,
): DesktopConnection {
  const desktopDevices = devices.filter((device) => device.kind === 'desktop');
  if (desktopDevices.some((device) => reachableDeviceIds.has(device.device_id))) {
    return { state: 'connected', desktopDevices };
  }
  if (desktopDevices.length > 0) return { state: 'connecting', desktopDevices };
  return { state: 'offline', desktopDevices };
}

export async function readDesktopConnection(
  bridge: Pick<BridgeResource, 'listConnectedDevices' | 'isConnected'>,
): Promise<DesktopConnection> {
  const devices = await bridge.listConnectedDevices();
  const desktops = devices.filter((device) => device.kind === 'desktop');
  const reachable = await Promise.all(
    desktops.map(async (device) => ((await bridge.isConnected(device.device_id)) ? device.device_id : null)),
  );
  return deriveDesktopConnection(
    devices,
    new Set(reachable.filter((id): id is string => id !== null)),
  );
}
